import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { useDCTypes, useSLocations } from "../../hooks/queries";
import {
  useCreateDCType,
  useCreateLocation,
  useUpdateDCType,
  useUpdateLocation,
} from "../../hooks/mutation";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const LocationNew = () => {
  const [page, setPage] = useState(1);

  const [isEdit, setIsEdit] = useState(false);
  const [location, setLocation] = useState(null);
  const [search, setSearch] = useState(null);
  const { data } = useSLocations(isEdit, search, page);
  const [locationsData, setLocationsData] = useState();
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    if (data) {
      setLocationsData(data.data);
      setTotalCount(data.count);
    }
  }, [data]);

  const [companies, setCompanies] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      LocationTypeID: "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    },
  });

  useEffect(() => {
    if (location) {
      reset({
        CompanyID: location.CompanyID,
        LocationTypeID: location.LocationTypeID,
        Code: location.Code,
        Description: location.Description,
        MinutesFromAirport: location.MinutesFromAirport,
      });
    }
  }, [location, reset]);

  useEffect(() => {
    const fetchSLocations = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      const { data: LocationTypes, error: LError } = await supabase
        .from("LocationTypes")
        .select("LocationTypeID, Code");
      if (cError) {
        setCompanies([]);
      } else if (LError) {
        setLocationTypes([]);
      } else {
        setCompanies(Companies);
        setLocationTypes(LocationTypes);
        reset({
          CompanyID: Companies[0] ? Companies[0].CompanyID : "",
          LocationTypeID: LocationTypes[0]
            ? LocationTypes[0].LocationTypeID
            : "",
          Code: "",
          Description: "",
          MinutesFromAirport: "",
        });
      }
    };
    fetchSLocations();
  }, [reset]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => old - 1);
  }, []);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
  }, []);
  const onError = useCallback((err) => {
    console.log("error", err);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      LocationTypeID: locationTypes[0] ? locationTypes[0].LocationTypeID : "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    });
    setIsEdit(true);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setLocation();
  };

  const { mutate: createLocation, isPending: isCreating } = useCreateLocation(
    onSuccess,
    onError
  );

  const { mutate: updateLocation, isPending: isUpdating } = useUpdateLocation(
    onSuccess,
    onError
  );

  const OnCreate = useCallback(
    (data) => {
      if (location) {
        updateLocation({ ...data, ID: location.LocationID });
      } else {
        createLocation(data);
      }
    },
    [createLocation, location, updateLocation]
  );
  const OnSearch = useCallback(
    (data) => {
      setSearch(data);
    },
    [setSearch]
  );

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Company</div>
            <div className="w-35">
              <Controller
                control={control}
                name="CompanyID"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CompanyID?.message}
                  >
                    {companies.map((company) => (
                      <option key={company.CompanyID} value={company.CompanyID}>
                        {company.Name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>LocationType</div>
            <div className="w-35">
              <Controller
                control={control}
                name="LocationTypeID"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CompanyID?.message}
                  >
                    {locationTypes.map((locationType) => (
                      <option
                        key={locationType.LocationTypeID}
                        value={locationType.LocationTypeID}
                      >
                        {locationType.Code}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Code</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Code"
                rules={{
                  required: "Code is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Code?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Description</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Description"
                rules={{
                  required: "Description is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Description?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Minutes From Airport</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="MinutesFromAirport"
                rules={{
                  required: "MinutesFromAirport is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="number"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.MinutesFromAirport?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={isCreating | isUpdating}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{location ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Location List</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>

          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />

          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Company Name</th>
                <th className="px-4 py-2 border">LocationType</th>
                <th className="px-4 py-2 border">Code</th>
                <th className="px-4 py-2 border">Description</th>
                <th className="px-4 py-2 border">Minutes From Airport</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {locationsData &&
                locationsData.map((location) => (
                  <tr key={location.LocationID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{location.LocationID}</td>
                    <td className="px-4 py-2 border">
                      {location.Company.Name}
                    </td>
                    <td className="px-4 py-2 border">
                      {location.LocationType.Code}
                    </td>
                    <td className="px-4 py-2 border">{location.Code}</td>
                    <td className="px-4 py-2 border">{location.Description}</td>
                    <td className="px-4 py-2 border">
                      {location.MinutesFromAirport}
                    </td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setLocation(location);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "LocationID",
      which: "contains",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"LocationID"}>LocationID</option>
                <option value={"Name"}>Company</option>
                <option value={"LCode"}>LocationType</option>
                <option value={"Code"}>Code</option>
                <option value={"Description"}>Description</option>
                <option value={"MinutesFromAirport"}>MinutesFromAirport</option>
              </select>
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <p className="mb-0">Which:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="which"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value="contains">contains</option>
                <option value="starts with">starts with</option>
                <option value="equals">equals</option>
                <option value="ends with">ends with</option>
              </select>
            )}
          />
        </div>
        <div className="">
          <Controller
            control={searchControl}
            name="text"
            rules={{
              required: "Required",
            }}
            render={({ field: { value, onChange } }) => (
              <input
                type="text"
                value={value}
                onChange={onChange}
                className="custom-input rounded-lg w-100 "
              />
            )}
          />
        </div>
      </div>
      <div className="flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({
                type: "DriverlocationID",
                which: "contains",
                text: "",
              });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
